/** @jsx isolateComponent */

import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import keycode from 'keycode';
import type { Interpolation } from '@emotion/css';

import { ServiceTypeahead } from '@zapier/design-system-patterns';
import { useClickOutside } from '@zapier/design-system';
import { useLink } from '@zapier/common-hooks';
import { useTrackingContext } from '@zapier/toolbox-tracking';
import { isolateComponent, labelStyles } from '@zapier/common-utils';
import {
  searchHasNoResults,
  searchHasResults,
  searchResultClick,
} from '@zapier/universal-nav/dist/constants';

import SearchToggler from './SearchToggler';

export type Props = {
  /*
   * Indicates whether the user is currently searching.
   */
  isSearching: boolean;
  /*
   * The `id` attribute of the search `input`, used for hooking
   * `input` and `label` together.
   */
  searchInputId: string;
  /*
   * Dictates what happens when searching is closed.
   */
  onClose: () => void;
};

const Styles = labelStyles(__filename, {
  root: () => [
    {
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      gap: 10,
      width: '100%',
      maxWidth: 500,
    },
  ],
});

const SearchForm = (props: Props) => {
  const { trackEvent } = useTrackingContext();
  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: () => props.onClose(),
  });
  const onEscape = (e: ReactKeyboardEvent) => {
    if (e.keyCode === keycode('escape')) {
      props.onClose();
    }
  };
  const { routeTo } = useLink();
  return (
    <div css={Styles.root() as Interpolation}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div onKeyUp={onEscape} ref={setClickOutsideNode}>
        <ServiceTypeahead
          ariaLabel="Search Apps"
          excludeUpcoming={false}
          inputId={props.searchInputId}
          menuAriaLabel="List of available apps"
          onChange={app => {
            if (app && app.slug && routeTo) {
              const url = `/apps/${app.slug}`;
              trackEvent(searchResultClick, { slug: app.slug });
              routeTo(url);
            }
          }}
          onResultsUpdate={(query, results) => {
            trackEvent(results.length ? searchHasResults : searchHasNoResults, {
              searchTerm: query,
            });
          }}
          placeholder="Search by App"
        />
      </div>
      <SearchToggler
        ariaHidden={!props.isSearching}
        ariaLabel="Close search"
        disabled={!props.isSearching}
        isSearching={true}
        onClick={() => {
          props.onClose();
        }}
      />
    </div>
  );
};

export default SearchForm;
